import imageCompression from 'browser-image-compression';

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

/**
 * Compress and resize an image file for upload
 * - Max dimension: 1600px
 * - JPEG quality: 80% (75-85% range)
 * - Preserves EXIF data including timestamp
 * 
 * @param file - The original image file
 * @returns Compression result with original and compressed file info
 */
export async function compressImage(file: File): Promise<CompressionResult> {
  const originalSize = file.size;

  const options = {
    maxSizeMB: 2, // Maximum file size in MB (fallback limit)
    maxWidthOrHeight: 1600, // Maximum dimension (width or height)
    useWebWorker: true, // Use web worker for better performance
    initialQuality: 0.8, // JPEG quality (80% = middle of 75-85% range)
    preserveExif: true, // Preserve EXIF metadata including timestamp
  };

  try {
    const compressedFile = await imageCompression(file, options);
    const compressedSize = compressedFile.size;
    const compressionRatio = ((originalSize - compressedSize) / originalSize) * 100;

    return {
      file: compressedFile,
      originalSize,
      compressedSize,
      compressionRatio,
    };
  } catch (error) {
    console.error('Image compression failed:', error);
    // If compression fails, return original file
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      compressionRatio: 0,
    };
  }
}

/**
 * Format file size in human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "2.5 MB", "350 KB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}
